import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, Fuel, Sparkles, Home as HomeIcon, Building2, MapPin } from "lucide-react";
import { deriveChargeKwh } from "@/lib/efficiency";
import { deriveChargeMetrics } from "@/lib/chargingCalc";
import { todayInTz } from "@/lib/tz";
import useTelemetryAutoFill from "@/lib/useTelemetryAutoFill";
import { computeChargeCost, rateHintFor, resolveRate } from "@/lib/chargingRates";

// Whether recalc-on-demand would yield a result for the active profile.
// Used to gate the persistent "Recalc from … rate" link after a manual
// override (Flat/TOU need a resolvable current rate; tiered/monthly are
// considered ready as long as basic config is present).
// `gridKwh` MUST be grid energy (post-loss), never raw battery energy.
const canRecalcFor = (profile, gridKwh, entryDate) => {
  if (!profile?.profile_type) return false;
  const t = profile.profile_type;
  if (t === "monthly") return Number.isFinite(parseFloat(profile.monthly_cost));
  if (t === "tiered") {
    const c = computeChargeCost({ profile, kwh: gridKwh, when: new Date(), entryDate });
    return !!c && Number.isFinite(c.cost);
  }
  return !!resolveRate(profile, new Date());
};

/**
 * AddEnergyDialog — log a charging session or fuel fill-up.
 * Adapts the form layout based on vehicle.energy_type:
 *  - EV       → Charge only (Battery %, kWh added, Cost, Odometer readings)
 *  - Gas      → Fuel only   (Liters/Gallons, Cost, Price/unit, Odometer readings)
 *  - Hybrid   → Segmented Charge | Fuel toggle visible at the top
 *
 * Auto-derives values from the best available inputs but always allows manual
 * override (manual edits are remembered via a "userEdited" flag).
 */
export default function AddEnergyDialog({
  open,
  onOpenChange,
  vehicles,
  defaultVehicleId,
  defaultType = null,
  initialEvent = null,   // when present → edit mode
  currencySymbol = "$",
  tz,                    // IANA timezone; default date is computed in this zone.
  chargingRates = null,  // { home, work } — from settings.charging_rates
  onSubmit,
}) {
  const isEdit = !!initialEvent;
  const [vehicleId, setVehicleId] = useState(defaultVehicleId || "");
  const [type, setType] = useState("charge");
  const [date, setDate] = useState(todayInTz(tz));
  const [cost, setCost] = useState("");
  const [amount, setAmount] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [fuelUnit, setFuelUnit] = useState("L");
  const [startOdo, setStartOdo] = useState("");
  const [endOdo, setEndOdo] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [startBatt, setStartBatt] = useState("");
  const [endBatt, setEndBatt] = useState("");
  // Charging Rates v3 — Home/Work/Other selector + auto-cost-fill.
  const [locationTag, setLocationTag] = useState("home");

  // Manual override tracking — once a user types into a derived field,
  // we stop auto-overwriting it for the rest of this dialog session.
  const amountEditedRef = useRef(false);
  const costEditedRef = useRef(false);
  const ppuEditedRef = useRef(false);

  // Tracks which field the user touched last
  const lastEditedRef = useRef(null);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === vehicleId),
    [vehicles, vehicleId]
  );
  const eType = selectedVehicle?.energy_type || "ev";
  const capacity = selectedVehicle?.usable_battery_kwh;
  const isHybrid = eType === "hybrid";

  // v2.5 Auto-fill: Start Battery % in CHARGE mode is auto-populated from
  // the chronologically latest battery reading (trip OR charge — whichever
  // is more recent). Logic lives in the shared hook so both dialogs share
  // the same stale-closure-safe contract. EV / Hybrid only.
  const autoFill = useTelemetryAutoFill({
    open,
    isEdit,
    vehicleId,
    enabled: type === "charge" && (eType === "ev" || eType === "hybrid"),
    scope: "energy",
    fields: {
      batt: {
        sourceField: "current_battery_pct",
        setValue: setStartBatt,
      },
    },
  });

  useEffect(() => {
    if (!open) return;
    if (initialEvent) {
      // EDIT mode — pre-fill from existing event. In edit mode we treat the
      // user as having "manually set" every value so auto-derive won't clobber
      // their previously-saved fields unless they explicitly change one.
      setVehicleId(initialEvent.vehicle_id);
      setType(initialEvent.type || "charge");
      setDate(initialEvent.date);
      setCost(initialEvent.cost != null ? String(initialEvent.cost) : "");
      setAmount(initialEvent.amount != null ? String(initialEvent.amount) : "");
      setPricePerUnit(initialEvent.price_per_unit != null ? String(initialEvent.price_per_unit) : "");
      const u = initialEvent.unit;
      setFuelUnit(u === "gal" ? "gal" : "L");
      setStartOdo(initialEvent.start_odometer_km != null ? String(initialEvent.start_odometer_km) : "");
      setEndOdo(initialEvent.end_odometer_km != null ? String(initialEvent.end_odometer_km) :
                 initialEvent.odometer_km != null ? String(initialEvent.odometer_km) : "");
      setNotes(initialEvent.notes || "");
      setStartBatt(initialEvent.start_battery_pct != null ? String(initialEvent.start_battery_pct) : "");
      setEndBatt(initialEvent.end_battery_pct != null ? String(initialEvent.end_battery_pct) : "");
      setLocationTag(initialEvent.location_tag || "home");
      amountEditedRef.current = true;
      costEditedRef.current = true;
      ppuEditedRef.current = true;
      // Lock auto-fill against ever overwriting saved telemetry.
      autoFill.lockForEdit();
      return;
    }
    const v0 = vehicles.find((v) => v.id === defaultVehicleId) || vehicles[0];
    setVehicleId(v0?.id || "");
    // Priority: explicit defaultType from parent → vehicle's natural default → charge
    const naturalDefault = (v0?.energy_type || "ev") === "gas" ? "fuel" : "charge";
    const t0 = defaultType && (defaultType === "charge" || defaultType === "fuel")
      ? defaultType
      : naturalDefault;
    setType(t0);
    setDate(todayInTz(tz));
    setCost("");
    setAmount("");
    setPricePerUnit("");
    setFuelUnit("L");
    setStartOdo("");
    setEndOdo("");
    setNotes("");
    setStartBatt("");
    setEndBatt("");
    setLocationTag("home");
    amountEditedRef.current = false;
    costEditedRef.current = false;
    ppuEditedRef.current = false;
    lastEditedRef.current = null;
  }, [open, defaultVehicleId, vehicles, defaultType, initialEvent]);

  // When vehicle changes, force type to match for non-hybrid vehicles
  useEffect(() => {
    if (!selectedVehicle) return;
    if (eType === "ev") setType("charge");
    else if (eType === "gas") setType("fuel");
    // hybrid → leave user's selection
  }, [vehicleId, eType, selectedVehicle]);

  // Reset manual-override flags when switching type
  useEffect(() => {
    amountEditedRef.current = false;
    costEditedRef.current = false;
    ppuEditedRef.current = false;
    lastEditedRef.current = null;
  }, [type]);

  const isFuel = type === "fuel";
  const amountUnit = isFuel ? fuelUnit : "kWh";
  const amountLabel = isFuel
    ? `${fuelUnit === "L" ? "Liters" : "Gallons"} filled`
    : "kWh added";

  // --- CHARGE auto-derivation: kWh added from battery delta × capacity
  const derivedChargeKwh = useMemo(
    () => (!isFuel ? deriveChargeKwh(startBatt, endBatt, capacity) : null),
    [isFuel, startBatt, endBatt, capacity]
  );

  useEffect(() => {
    if (!isFuel && derivedChargeKwh != null && !amountEditedRef.current) {
      setAmount(derivedChargeKwh.toFixed(2));
    }
  }, [derivedChargeKwh, isFuel]);

  // --- FUEL auto-derivation: price/unit ↔ cost ↔ amount (fill the unset one)
  useEffect(() => {
    if (!isFuel) return;

    const c = parseFloat(cost);
    const a = parseFloat(amount);
    const p = parseFloat(pricePerUnit);

    const hasC = Number.isFinite(c) && c > 0;
    const hasA = Number.isFinite(a) && a > 0;
    const hasP = Number.isFinite(p) && p > 0;

    // Clear stale derived values
    if (!hasC && lastEditedRef.current === "cost") {
      setPricePerUnit("");
      return;
    }

    if (!hasP && lastEditedRef.current === "ppu") {
      setCost("");
      return;
    }

    if (!hasA && lastEditedRef.current === "amount") {
      return;
    }

    // Cost + Amount => Price/L
    if (
      lastEditedRef.current === "cost" &&
      hasC &&
      hasA
    ) {
      const next = (c / a).toFixed(3);

      if (pricePerUnit !== next) {
        setPricePerUnit(next);
      }

      return;
    }

    // Price/L + Amount => Cost
    if (
      lastEditedRef.current === "ppu" &&
      hasP &&
      hasA
    ) {
      const next = (a * p).toFixed(2);

      if (cost !== next) {
        setCost(next);
      }

      return;
    }

    // Amount changed
    if (
      lastEditedRef.current === "amount"
    ) {
      if (hasA && hasP) {
        const next = (a * p).toFixed(2);

        if (cost !== next) {
          setCost(next);
        }
      } else if (hasA && hasC) {
        const next = (c / a).toFixed(3);

        if (pricePerUnit !== next) {
          setPricePerUnit(next);
        }
      }
    }

    // Cost + Price/L => Amount
    if (
      lastEditedRef.current === "cost" &&
      hasC &&
      hasP &&
      !hasA
    ) {
      setAmount((c / p).toFixed(2));
    }

    if (
      lastEditedRef.current === "ppu" &&
      hasC &&
      hasP &&
      !hasA
    ) {
      setAmount((c / p).toFixed(2));
    }

  }, [isFuel, cost, amount, pricePerUnit]);

  // Distance derived from odometer readings (used to show trip-level efficiency hint)
  const distanceKm = useMemo(() => {
    const s = parseFloat(startOdo);
    const e = parseFloat(endOdo);
    if (!isFinite(s) || !isFinite(e) || e <= s) return null;
    return e - s;
  }, [startOdo, endOdo]);

  // Efficiency preview: best data for the vehicle type
  const effPreview = useMemo(() => {
    if (distanceKm == null) return null;
    const a = parseFloat(amount);
    if (!isFinite(a) || a <= 0) return null;
    if (isFuel) {
      const litres = fuelUnit === "gal" ? a * 3.785411784 : a;
      const lp100 = (litres / distanceKm) * 100;
      return { label: "L/100km", value: lp100.toFixed(1) };
    }
    // charge: distance ÷ kWh added (only a rough indicator if user added kWh between trips)
    const kmPerKwh = distanceKm / a;
    return { label: "km/kWh", value: kmPerKwh.toFixed(1) };
  }, [distanceKm, amount, fuelUnit, isFuel]);

  // ---------- Charging Rates v3 ----------
  // Resolve the configured profile for the selected location (home/work only).
  const activeProfile = useMemo(() => {
    if (type !== "charge") return null;
    if (locationTag === "home") return chargingRates?.home || null;
    if (locationTag === "work") return chargingRates?.work || null;
    return null;
  }, [type, locationTag, chargingRates]);

  // Hint string shown under the location selector (e.g. "Time-of-Use · off-peak now ($0.07/kWh · 11 PM – 7 AM)").
  const rateHint = useMemo(() => {
    if (type !== "charge") return null;
    return rateHintFor({ locationTag, profile: activeProfile, when: new Date(), entryDate: date });
  }, [type, locationTag, activeProfile, date]);

  // Central charging analytics — SINGLE source of truth for grid energy,
  // efficiency, loss, and inferred charger/temp categories. Every downstream
  // cost calc + preview reads from here, mirroring the server-side
  // charging_calc.derive_charge_metrics helper. Do NOT duplicate this math
  // in another useMemo / handler.
  const chargingMetrics = useMemo(() => {
    if (type !== "charge") return null;
    return deriveChargeMetrics({
      batteryEnergyKwh: amount,
      capacityKwh: capacity,
      startBatteryPct: startBatt,
      endBatteryPct: endBatt,
      dateIso: date,
      timezone: tz,
      locationChargerPref: activeProfile?.charger_type,
      vehicleDefaultCharger: selectedVehicle?.default_charger_type,
    });
  }, [type, amount, capacity, startBatt, endBatt, date, tz, activeProfile, selectedVehicle]);

  // Grid energy consumed (kWh) — Battery Energy ÷ Efficiency. This is the
  // figure that MUST be passed to `computeChargeCost` (never raw `amount`)
  // so charging losses correctly inflate the billed cost. See spec.
  const gridEnergyKwh = chargingMetrics?.grid_energy_kwh ?? null;

  // Auto-fill Cost from configured rate * kWh. We respect the user's manual
  // overrides via costEditedRef — once they type into Cost, we never touch
  // it again unless they click Recalc. Initial entries trigger auto-fill
  // when amount changes OR location changes.
  const [autoFilledCost, setAutoFilledCost] = useState(false);
  const [autoFillKind, setAutoFillKind] = useState(null);  // "flat" | "tou" | "tiered" | "monthly"
  useEffect(() => {
    if (type !== "charge") return;
    if (locationTag === "other") return;
    if (costEditedRef.current) return;
    // IMPORTANT: bill on GRID energy, not raw battery energy. gridEnergyKwh
    // already accounts for charger_type × temperature efficiency losses.
    const res = computeChargeCost({
      profile: activeProfile,
      kwh: gridEnergyKwh,
      when: new Date(),
      entryDate: date,
    });
    if (res?.out_of_range) {
      // Tiered profile but the entry date isn't within the configured month
      // range — leave Cost blank and let the hint banner explain.
      if (autoFilledCost) {
        setCost("");
        setAutoFilledCost(false);
        setAutoFillKind(null);
      }
      return;
    }
    if (res && Number.isFinite(res.cost)) {
      setCost(res.cost.toFixed(2));
      setAutoFilledCost(true);
      setAutoFillKind(res.profile_type || null);
    } else if (autoFilledCost) {
      // Previously auto-filled but the rate/kwh became invalid — clear it.
      setCost("");
      setAutoFilledCost(false);
      setAutoFillKind(null);
    }
     
  }, [type, locationTag, activeProfile, gridEnergyKwh, date]);

  // Force-recalc helper (used by the "Recalc" link beneath the Cost field).
  const recalcCost = () => {
    if (type !== "charge" || locationTag === "other") return;
    const res = computeChargeCost({
      profile: activeProfile,
      kwh: gridEnergyKwh,
      when: new Date(),
      entryDate: date,
    });
    if (res?.out_of_range) return;
    if (res && Number.isFinite(res.cost)) {
      costEditedRef.current = false;
      setCost(res.cost.toFixed(2));
      setAutoFilledCost(true);
      setAutoFillKind(res.profile_type || null);
    }
  };

  const handleAmountChange = (v) => {
    amountEditedRef.current = true;
    lastEditedRef.current = "amount";
    setAmount(v);
  };
  const handleCostChange = (v) => {
    costEditedRef.current = true;
    lastEditedRef.current = "cost";
    setAutoFilledCost(false);
    setCost(v);
  };
  const handlePpuChange = (v) => {
    ppuEditedRef.current = true;
    lastEditedRef.current = "ppu";
    setPricePerUnit(v);
  };

  const submit = async () => {
    if (!vehicleId || !date) return;
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: vehicleId,
        type,
        date,
        cost: parseFloat(cost) || 0,
        amount: parseFloat(amount) || 0,
        unit: amountUnit,
        notes,
        // Stamp active timezone for audit. Persisted per-row.
        entry_timezone: tz || null,
      };
      if (startOdo) payload.start_odometer_km = parseFloat(startOdo);
      if (endOdo) {
        payload.end_odometer_km = parseFloat(endOdo);
        payload.odometer_km = parseFloat(endOdo);
      }
      if (pricePerUnit) payload.price_per_unit = parseFloat(pricePerUnit);
      if (!isFuel && (startBatt || endBatt)) {
        payload.start_battery_pct = startBatt ? parseFloat(startBatt) : null;
        payload.end_battery_pct = endBatt ? parseFloat(endBatt) : null;
      }
      // Charge-only: location_tag drives the dashboard deductible-energy card
      // and the auto-cost-fill we did up-stream.
      if (!isFuel) {
        payload.location_tag = locationTag;
      }
      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[520px] max-h-[92vh] overflow-y-auto"
        data-testid="add-energy-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            {isEdit ? (isFuel ? "Edit Fuel Entry" : "Edit Charging Entry") : "Log Energy"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            {isFuel
              ? "Record a fuel fill-up."
              : "Record a charging session."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Vehicle */}
          <div className="space-y-2">
            <Label className="label-tiny">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId} disabled={isEdit}>
              <SelectTrigger
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="energy-vehicle-select"
              >
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                    {v.energy_type && (
                      <span className="text-[var(--text-muted)] ml-2 text-xs">· {v.energy_type}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type segmented — prominently at top. Hybrid only. */}
          {isHybrid ? (
            <div>
              <Label className="label-tiny mb-2 block">Entry Type</Label>
              <div
                className="grid grid-cols-2 bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5 gap-0.5"
                style={{ borderRadius: 2 }}
                data-testid="energy-type-toggle"
              >
                <button
                  type="button"
                  onClick={() => setType("charge")}
                  data-testid="energy-type-charge"
                  className={`tap inline-flex items-center justify-center gap-1.5 py-2 text-xs tracking-wider transition-colors ${
                    type === "charge"
                      ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                  style={{ borderRadius: 1 }}
                >
                  <Zap className="w-3.5 h-3.5" strokeWidth={2} />
                  Charge
                </button>
                <button
                  type="button"
                  onClick={() => setType("fuel")}
                  data-testid="energy-type-fuel"
                  className={`tap inline-flex items-center justify-center gap-1.5 py-2 text-xs tracking-wider transition-colors ${
                    type === "fuel"
                      ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                  style={{ borderRadius: 1 }}
                >
                  <Fuel className="w-3.5 h-3.5" strokeWidth={2} />
                  Fuel
                </button>
              </div>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-wider uppercase text-[var(--text-secondary)] bg-[var(--bg-input)] border border-[var(--border-subtle)]"
              style={{ borderRadius: 999 }}
              data-testid="energy-type-badge"
            >
              {isFuel ? <Fuel className="w-3 h-3" strokeWidth={2} /> : <Zap className="w-3 h-3" strokeWidth={2} />}
              {isFuel ? "Fuel Entry" : "Charging Entry"}
            </div>
          )}

          {/* Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="label-tiny">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="energy-date-input"
              />
            </div>
            {isFuel && (
              <div className="space-y-2">
                <Label className="label-tiny">Fuel Unit</Label>
                <div
                  className="inline-flex items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5 h-9"
                  style={{ borderRadius: 2 }}
                >
                  {["L", "gal"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setFuelUnit(u)}
                      data-testid={`energy-fuel-unit-${u}`}
                      className={`tap px-3 h-7 text-xs font-mono-data tracking-wider transition-colors ${
                        fuelUnit === u
                          ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                          : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                      style={{ borderRadius: 1 }}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* === Charging Location (charge only) === */}
          {!isFuel && (
            <div className="space-y-2" data-testid="energy-location-section">
              <Label className="label-tiny">Charging Location</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "home", label: "Home", emoji: "🏠", accent: "#34D17A", accentBg: "rgba(52, 209, 122, 0.10)", accentBorder: "rgba(52, 209, 122, 0.35)" },
                  { value: "work", label: "Work", emoji: "🏢", accent: "#5AB1FF", accentBg: "rgba(90, 177, 255, 0.10)", accentBorder: "rgba(90, 177, 255, 0.35)" },
                  { value: "other", label: "Other", emoji: "📍", accent: "#FFB340", accentBg: "rgba(255, 179, 64, 0.10)", accentBorder: "rgba(255, 179, 64, 0.35)" },
                ].map((opt) => {
                  const active = locationTag === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setLocationTag(opt.value)}
                      data-testid={`energy-location-${opt.value}`}
                      aria-pressed={active}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 border transition-colors"
                      style={{
                        borderRadius: 3,
                        background: active ? opt.accentBg : "var(--bg-input)",
                        borderColor: active ? opt.accentBorder : "var(--border-subtle)",
                      }}
                    >
                      <span className="text-base leading-none">{opt.emoji}</span>
                      <span
                        className="text-xs tracking-tight"
                        style={{ color: active ? opt.accent : "var(--text-muted)" }}
                      >
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {rateHint && (
                <div
                  className="flex items-center gap-1.5 text-[11px] leading-relaxed pt-0.5"
                  data-testid="energy-location-hint"
                  style={{
                    color:
                      rateHint.tone === "ok" ? "#34D17A" :
                      rateHint.tone === "warn" ? "#FFB340" :
                      "var(--text-muted)",
                  }}
                >
                  <Zap className="w-3 h-3 flex-shrink-0" strokeWidth={2} fill="currentColor" />
                  <span>{rateHint.text}</span>
                </div>
              )}
            </div>
          )}

          {/* === CHARGE-only fields === */}
          {!isFuel && (
            <div
              className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-3 space-y-3"
              style={{ borderRadius: 2 }}
              data-testid="energy-charge-section"
            >
              <div className="label-tiny">Battery</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="label-tiny h-[24px] flex items-center gap-1.5">
                    <span>Start %</span>
                    {autoFill.flags.batt && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase text-[#34D17A] font-mono-data"
                        data-testid="energy-start-batt-auto"
                        title="Auto-filled from your last trip's end battery %"
                      >
                        <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                        Auto
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={startBatt}
                    onChange={(e) => {
                      autoFill.markUserEdited("batt");
                      setStartBatt(e.target.value);
                    }}
                    placeholder="—"
                    className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                    data-testid="energy-start-batt"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="label-tiny">End %</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={endBatt}
                    onChange={(e) => setEndBatt(e.target.value)}
                    placeholder="—"
                    className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                    data-testid="energy-end-batt"
                  />
                </div>
              </div>
              {derivedChargeKwh != null && !amountEditedRef.current && (
                <div className="text-[11px] text-[var(--text-secondary)]" data-testid="energy-derived-kwh">
                  Auto-fill: <span className="font-mono-data text-[var(--text-primary)]">{derivedChargeKwh.toFixed(2)} kWh</span> · edit below to override
                </div>
              )}
              {!capacity && (
                <p className="text-[11px] text-[#FFB340] leading-snug">
                  Set this vehicle's Usable Battery Capacity to auto-fill kWh from battery %.
                </p>
              )}
            </div>
          )}

          {/* Amount + Cost row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="label-tiny">
                {amountLabel} {isFuel ? `(${fuelUnit})` : "(kWh)"}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.0"
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="energy-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tiny">Cost ({currencySymbol})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={cost}
                onChange={(e) => handleCostChange(e.target.value)}
                placeholder="0.00"
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="energy-cost-input"
              />
              {!isFuel && locationTag !== "other" && (
                autoFilledCost ? (
                  <div
                    className="flex items-center justify-between gap-2 text-[11px] leading-tight"
                    data-testid="energy-cost-autofill-badge"
                  >
                    <span className="flex items-center gap-1 text-[#FFB340]">
                      <Zap className="w-3 h-3 flex-shrink-0" strokeWidth={2} fill="currentColor" />
                      {autoFillKind === "monthly"
                        ? `Covered by monthly plan · $${parseFloat(activeProfile?.monthly_cost || 0).toFixed(2)}/mo`
                        : autoFillKind === "tiered"
                        ? `Est. from ${locationTag === "home" ? "Home" : "Work"} tiered rate`
                        : `Est. from ${locationTag === "home" ? "Home" : "Work"} rate`}
                    </span>
                    {autoFillKind !== "monthly" && (
                      <button
                        type="button"
                        onClick={recalcCost}
                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] underline underline-offset-2"
                        data-testid="energy-cost-recalc-btn"
                      >
                        Recalc
                      </button>
                    )}
                  </div>
                ) : (
                  // After a manual override the badge clears. Surface a
                  // persistent Recalc link IF a rate is still resolvable
                  // for the active location, so users can return to the
                  // rate-derived cost on demand.
                  canRecalcFor(activeProfile, gridEnergyKwh, date) && (
                    <button
                      type="button"
                      onClick={recalcCost}
                      className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] underline underline-offset-2"
                      data-testid="energy-cost-recalc-btn"
                    >
                      Recalc from {locationTag === "home" ? "Home" : "Work"} rate
                    </button>
                  )
                )
              )}
            </div>
          </div>

          {/* === FUEL-only: Price per unit === */}
          {isFuel && (
            <div className="space-y-2">
              <Label className="label-tiny">
                Price per {fuelUnit} ({currencySymbol}/{fuelUnit}) · optional
              </Label>
              <Input
                type="number"
                step="0.001"
                min="0"
                value={pricePerUnit}
                onChange={(e) => handlePpuChange(e.target.value)}
                placeholder="0.000"
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="energy-price-per-unit"
              />
              {pricePerUnit && !ppuEditedRef.current && (
                <p className="text-[11px] text-[var(--text-secondary)]" data-testid="energy-ppu-hint">
                  Auto-derived from Cost ÷ {fuelUnit} · edit to override
                </p>
              )}
            </div>
          )}

          {/* Odometer readings (both types) */}
          <div
            className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-3 space-y-3"
            style={{ borderRadius: 2 }}
            data-testid="energy-odometer-section"
          >
            <div className="label-tiny">Odometer Readings (km) · optional</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-tiny">Start</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={startOdo}
                  onChange={(e) => setStartOdo(e.target.value)}
                  placeholder="—"
                  className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                  data-testid="energy-start-odo"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">End</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={endOdo}
                  onChange={(e) => setEndOdo(e.target.value)}
                  placeholder="—"
                  className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                  data-testid="energy-end-odo"
                />
              </div>
            </div>
            {effPreview && (
              <div
                className="text-[11px] text-[var(--text-secondary)]"
                data-testid="energy-efficiency-preview"
              >
                Since last reading:{" "}
                <span className="font-mono-data text-[var(--text-primary)]">
                  {distanceKm.toFixed(0)} km · {effPreview.value} {effPreview.label}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="label-tiny">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                isFuel
                  ? "Station name, brand, octane..."
                  : "Home charging, supercharger, station name..."
              }
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] min-h-[60px]"
              data-testid="energy-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            data-testid="energy-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !vehicleId || !date}
            className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
            data-testid="energy-submit-btn"
          >
            {submitting ? "Saving..." : (isEdit ? "Save Changes" : "Save Entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
